import {
  Button,
  Flex,
  Icon,
  Text,
  IconButton,
  CircularProgress,
} from '@chakra-ui/react';
import { ReactComponent as UploadIcon } from 'resources/images/uploadIcon.svg';
import { ReactComponent as CloseIcon } from 'resources/images/close.svg';
import { FileRejection, useDropzone } from 'react-dropzone';
import { useAppDispatch, useAppSelector } from 'app/store';
import { addAlertAction } from 'app/store/Alerts/slice';
import {
  getCurrentImportEffect,
  importCSVEffect,
} from 'app/store/Import/effects';
import { getCSV } from 'app/store/Import/selectors';
import { clearCsv } from 'app/store/Import/slice';
import { useEffect } from 'react';
import { acceptSx, activeSx, baseSx, closeBtnSx, rejectSx } from '../styles';
import { readCSV } from '../utils';
import { ImportLoader } from '../ImportLoader';
import { ErrorMessage } from './ErrorMessage';

export const ImportCSV = () => {
  const dispatch = useAppDispatch();
  const csv = useAppSelector(getCSV);

  useEffect(() => {
    dispatch(getCurrentImportEffect());
  }, [dispatch]);

  const onlyOneFileAllowed = () => {
    dispatch(
      addAlertAction({
        message: 'Only one csv file can be imported at a time.',
      })
    );
  };

  const {
    getRootProps,
    getInputProps,
    isDragActive,
    isDragAccept,
    isDragReject,
    open,
  } = useDropzone({
    accept: {
      'text/csv': ['.csv'],
    },
    noClick: true,
    multiple: false,
    onDropRejected: (rejections: FileRejection[]) => {
      if (rejections.length > 1) {
        onlyOneFileAllowed();
        return;
      }

      const rejection = rejections.find(({ errors }) => errors.length > 0);
      if (rejection) {
        dispatch(
          addAlertAction({
            message: `${rejection.file.name} has unsupported file type, please try another type.`,
          })
        );
      }
    },
    onDropAccepted: (acceptedFiles) => {
      if (acceptedFiles?.length > 1) {
        onlyOneFileAllowed();
      }

      if (acceptedFiles?.length !== 1) {
        return;
      }
      readCSV(acceptedFiles[0], async (csvText) => {
        await dispatch(
          importCSVEffect({ csvTitle: acceptedFiles[0].name, csvText })
        ).unwrap();
      });
    },
  });

  if (!csv?.status) {
    return (
      <Flex
        sx={{
          ...baseSx,
          ...(isDragActive ? activeSx : {}),
          ...(isDragAccept ? acceptSx : {}),
          ...(isDragReject ? rejectSx : {}),
        }}
        {...getRootProps()}
        data-testid="dragAndDrop-csv"
      >
        <input {...getInputProps()} />
        <Icon
          as={UploadIcon}
          color="P.50"
          flexShrink={0}
          height="86px"
          width="86px"
        />
        <Text
          fontSize="lg"
          fontWeight={500}
          color="M.70"
          mt={7}
          textAlign="center"
        >
          Drag and drop your csv file here
        </Text>
        <Text fontSize="md" color="M.60">
          or select a file from you file browser.
        </Text>
        <Button
          size="md"
          onClick={open}
          data-testid="browser-button-test"
          mt={6}
        >
          Browse
        </Button>
      </Flex>
    );
  }

  if (['PENDING', 'IN_PROGRESS'].includes(csv.status)) {
    return (
      <Flex sx={baseSx} data-testid="dragAndDrop-csv">
        {!csv?.rowCount ? (
          <CircularProgress
            isIndeterminate
            color="B.0"
            size="40px"
            thickness="7px"
          />
        ) : (
          <ImportLoader
            inProgress={csv.rowsProcessed || 0}
            count={csv.rowCount || 0}
            labelTemplate="Importing songs {inProgress} of {count}"
            w="80%"
          />
        )}
      </Flex>
    );
  }

  return (
    <Flex
      sx={{
        ...baseSx,
        h: 'auto',
      }}
    >
      <IconButton
        variant="secondary"
        aria-label="close"
        title="Clear CSV"
        size="xs"
        sx={closeBtnSx}
        onClick={() => dispatch(clearCsv())}
        icon={<CloseIcon />}
      />
      {csv?.error || ['FAILED_ONE', 'FAILED'].includes(csv?.status) ? (
        <ErrorMessage status={csv?.status} error={csv?.error} />
      ) : (
        <>
          <ImportLoader
            inProgress={csv.rowsProcessed || 0}
            count={csv.rowCount || 0}
            labelTemplate="Imported songs {inProgress} of {count}"
            w="80%"
          />
          All songs and meta data were imported successfully
          {csv?.path ? (
            <>
              {' from '}
              <Text as="span" color="B.0">
                {csv.path}
              </Text>
            </>
          ) : null}
        </>
      )}
    </Flex>
  );
};
